/**
 * FE-20: Admin Integrations API layer.
 * Maps to: GET /admin/metrics/integrations, GET /admin/metrics/agents
 */

import { apiFetch } from "./client";

// ─── Integration Types ───

export type IntegrationTypeKey =
  | "sdk"
  | "openclaw"
  | "claude_code"
  | "codex"
  | "cli";

export type IntegrationMetrics = {
  readonly integration_type: IntegrationTypeKey;
  readonly active_accounts: number;
  readonly active_agents: number;
  readonly messages: number;
  readonly deliveries: number;
  readonly errors: number;
  readonly disconnects: number;
};

export type VersionRow = {
  readonly version: string;
  readonly count: number;
  readonly percentage: number;
};

export type IntegrationDetail = {
  readonly metrics: IntegrationMetrics;
  readonly versions: ReadonlyArray<VersionRow>;
};

// ─── Agent Eco Types ───

export type AgentEcoMetrics = {
  readonly total_agents: number;
  readonly active_agents_24h: number;
  readonly total_messages_24h: number;
  readonly avg_messages_per_agent: number;
};

// ─── Params ───

export type IntegrationParams = {
  readonly integration_type?: IntegrationTypeKey;
  readonly start_date?: string;
  readonly end_date?: string;
};

// ─── API Calls ───

export async function fetchIntegrationMetrics(
  params: IntegrationParams,
): Promise<IntegrationDetail> {
  const qs = toQueryString(params);
  const res = await apiFetch<{
    data: { items: ReadonlyArray<IntegrationMetrics> };
  }>(`/admin/metrics/integrations?${qs}`);
  const items = res.data.items;
  const match = params.integration_type
    ? items.find((i) => i.integration_type === params.integration_type)
    : items[0];
  return {
    metrics: match ?? {
      integration_type: params.integration_type ?? "sdk",
      active_accounts: 0,
      active_agents: 0,
      messages: 0,
      deliveries: 0,
      errors: 0,
      disconnects: 0,
    },
    versions: [],
  };
}

export async function fetchAgentEcoMetrics(
  params: IntegrationParams,
): Promise<AgentEcoMetrics> {
  const qs = toQueryString(params);
  const res = await apiFetch<{
    data: { items: ReadonlyArray<AgentEcoMetrics> };
  }>(`/admin/metrics/agents?${qs}`);
  return (
    res.data.items[0] ?? {
      total_agents: 0,
      active_agents_24h: 0,
      total_messages_24h: 0,
      avg_messages_per_agent: 0,
    }
  );
}

// ─── Helpers ───

function toQueryString(obj: Record<string, string | undefined>): string {
  const entries = Object.entries(obj).filter(
    (pair): pair is [string, string] => pair[1] !== undefined,
  );
  return new URLSearchParams(entries).toString();
}
